import { Component, OnDestroy, OnInit } from '@angular/core';
import { EAssessmentSubTypes, EAssessmentTypes, ESectionRoutes, ESectionTypes } from '../../../shared/models/eop_types.enum';
import { Icons } from '../../../shared/helpers/icons';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { EopAssistantComponent } from '../../components/eop-assistant/eop-assistant.component';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { DeleteConfirmationComponent } from '../../components/delete-confirmation/delete-confirmation.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { IContact, IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { FormsModule } from '@angular/forms';
import { ContactsService } from '../../../shared/services/contact.service';
import { SendAlAssessmentComponent } from '../send-al-assessment/send-al-assessment.component';
import { HazardAnnexesService } from '../../../shared/services/hazardAnnexes.service';
import { IAssessment } from '../../../shared/models/assessment';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { NewHazardAssessmentComponent } from '../new-hazard-assessment/new-hazard-assessment.component';
import { EditHazardAssessmentComponent } from '../edit-hazard-assessment/edit-hazard-assessment.component';

@Component({
  selector: 'app-general-hazard-annexes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    EopAssistantComponent,
    IntroductionComponent,
    SpinnerComponent,
    PopupMessageComponent,
    PrvNextButtonsComponent,
    DeleteConfirmationComponent,
    SendAlAssessmentComponent,
    NgxPaginationModule,
    NewHazardAssessmentComponent,
    EditHazardAssessmentComponent
],
  templateUrl: './general-hazard-annexes.component.html',
  styleUrl: './general-hazard-annexes.component.css'
})
export class GeneralHazardAnnexesComponent implements OnInit, OnDestroy {

  eopSection: ESectionRoutes | null = null;
  showModalType: string = '';
  currentPageRoute: string = '';
  routerSub!: Subscription;

  icons = Icons;
  searchQuery: string = '';
  title: string = '';
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  currentAssessmentType: string = '';
  fullAssessmentsList: IAssessment[] = [];
  assessmentsList: IAssessment[] = [];
  messageModal: string | null = null;
  popupMessage: string = '';
  selectedAssessment: any | null = null;
  selectedSection: string | null = null;
  page = 1;
  itemsPerPage = 10;
  fullContactsList: IContact[] = [];
  
  assessmentByRoute: Record<string, string[]> = {
    'hazard-school-ground': [
      'AL-School Building',
      'AL-Playground',
      'AL-School Grounds',
      'AL-Other Structures'
    ],
    'hazard-classroom': [ 'AL-Classroom' ],
    'hazard-evacuation-routes': [ 'AL-Evacuation Routes' ],
    'hazard-neighborhoods': [ 'AL-Neighborhoods' ]
  };

  loading: boolean = false;
  sortOrder: boolean = true;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private hazardAnnexesService: HazardAnnexesService
  ) {}
  
  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    
    this.routerSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.initializeFromRoute());

    this.initializeFromRoute();

    console.log('current route:', this.currentPageRoute);
    this.loadContacts();
  }

  ngOnDestroy() {
    this.routerSub?.unsubscribe();
  }

  initializeFromRoute() {
    this.currentPageRoute = this.route.snapshot.routeConfig?.path?.split('/').pop() || '';    
    if (this.currentPageRoute == 'hazard-school-ground') {
      this.currentAssessmentType = 'AL-School Grounds';
    } else if ( this.currentPageRoute == 'hazard-classroom') {
      this.currentAssessmentType = 'AL-Classroom';
    } else if ( this.currentPageRoute == 'hazard-evacuation-routes') {
      this.currentAssessmentType = 'AL-Evacuation Routes';
    } else if ( this.currentPageRoute == 'hazard-neighborhoods') {
      this.currentAssessmentType = 'AL-Neighborhoods';
    }

    // Get ID from route
    this.eopSection = ESectionRoutes[this.currentPageRoute as keyof typeof ESectionRoutes];
    // Then get title from ESectionTypes using that ID
    this.title = String(ESectionTypes[this.eopSection as unknown as keyof typeof ESectionTypes]) || '';

    console.log('Resolved title:', this.title);

    const sections = this.assessmentByRoute[this.currentPageRoute] || [];
    // Default selection
    // this.selectedSection = sections[0] || null;
  }

  // load all Contacts and Contacts of a department
  loadContacts(): void {
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loading = false;
        this.fullContactsList = response.data || [];
        console.log('Contacts loaded successfully:', response);
        this.loadBuildingAssessments();
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  };

  loadBuildingAssessments(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.hazardAnnexesService.getALAssessments(this.currentBuildingId, String(EAssessmentTypes[this.currentAssessmentType as keyof typeof EAssessmentTypes])).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            console.log('Assessments loaded successfully:', response);
            this.fullAssessmentsList = response.data || [];
            this.assessmentsList = this.fullAssessmentsList.map((assessment: any) => {
              const contact = this.fullContactsList.find(c => c.id === assessment.contact_id);
              return {
                ...assessment,
                contact_name: contact ? contact.name : 'N/A'
              };
            });
            this.fullAssessmentsList = [...this.assessmentsList];
            // Filter by selectedSection's enum ID
            // const subtypeId = EAssessmentSubTypes[this.selectedSection as keyof typeof EAssessmentSubTypes];
            // this.assessmentsList = this.fullAssessmentsList.filter(a => a.sub_type_id === subtypeId);

            this.assessmentsList = [...this.fullAssessmentsList];
          }
        },
        error: (error: any) => {
          this.loading = false;
          console.error('Error loading assessments:', error.message);
        }
      })
    }
  }

  // onSectionChange(section: string): void {
  //   this.selectedSection = section;
  //   const subtypeId = EAssessmentSubTypes[section as keyof typeof EAssessmentSubTypes];
  //   this.assessmentsList = this.fullAssessmentsList.filter(a => a.sub_type_id === subtypeId);
  // }
  
  onSectionChange(section: string): void {
    this.selectedSection = section;
  
    if (!section) {
      // show all if user resets section
      this.assessmentsList = [...this.fullAssessmentsList];
      return;
    }
  
    const subtypeId = EAssessmentSubTypes[
      section as keyof typeof EAssessmentSubTypes
    ];
    
    this.assessmentsList = this.fullAssessmentsList.filter(
      a => a.sub_type_id === subtypeId
    );
  }

  // filterAssessmentsList(): void {
  //   const query = this.searchQuery.toLowerCase().trim();
  
  //   this.assessmentsList = this.fullAssessmentsList.filter((item) => {
  //     return (
  //       item.last_updated_at?.toLowerCase().includes(query) ||
  //       item.contact_name?.toString().toLowerCase().includes(query)
  //     );
  //   });
  // }

  filterAssessmentsList(): void {
    const query = this.searchQuery.toLowerCase().trim();
  
    // Base list: whatever is currently filtered by section
    let baseList = [...this.fullAssessmentsList];
  
    if (this.selectedSection) {
      const subtypeId = EAssessmentSubTypes[
        this.selectedSection as keyof typeof EAssessmentSubTypes
      ];
      baseList = baseList.filter(a => a.sub_type_id === subtypeId);
    }
  
    // Then apply search
    this.assessmentsList = baseList.filter((item) =>
      item.contact_name?.toLowerCase().includes(query) ||
      item.sub_type_name?.toLowerCase().includes(query) ||
      item.last_updated_user_name?.toLowerCase().includes(query) ||
      item.last_updated_at?.toLowerCase().includes(query)
    );
  }
  
  

  // sort assessment list
  sortList(key: keyof typeof this.assessmentsList[0]): void {
    this.sortOrder = !this.sortOrder;
    const subtypeId = EAssessmentSubTypes[this.selectedSection as keyof typeof EAssessmentSubTypes];
    this.assessmentsList = this.fullAssessmentsList.filter(a => a.sub_type_id === subtypeId);
    this.assessmentsList.sort((a, b) => {
      const valueA = a[key]?.toString().toLowerCase() || '';
      const valueB = b[key]?.toString().toLowerCase() || '';

      if (valueA < valueB) {
        return this.sortOrder ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortOrder ? 1 : -1;
      }
      return 0;
    });
  }

  handleOpenModal(modalName: string, selectedItem?: any): void {
    this.showModalType = modalName;
    this.selectedAssessment = selectedItem ? selectedItem : null;
  }

  handleCloseModal(modalName?: string): void {
    this.showModalType = '';
    this.selectedAssessment = null;
  }

  handleSave(message: {type: string, message: string}, actionType: string): void {
    this.handleCloseModal(actionType);
    this.loadBuildingAssessments();

    if (message.type === 'success') {
      this.messageModal = 'success';
    } else if (message.type === 'unsuccess') {
      this.messageModal = 'unsuccess';
    } 
    this.popupMessage = message.message;
  }

  confirmAssessments(): void {
    if (this.currentBuildingId) {
      this.hazardAnnexesService.confirmALAssessment(this.currentBuildingId, String(EAssessmentTypes[this.currentAssessmentType as keyof typeof EAssessmentTypes])).subscribe({
        next: (res) => {
          if (!res.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after confirming classroom assessments.';
            this.loadBuildingAssessments();
          }
        },
        error: (err) => {
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      })
    }
  }

  handleDelete(item: any): void {
    this.loading = true;
    if (this.currentBuildingId && item.id) {
      this.hazardAnnexesService.deleteAssess(item.id.toString()).subscribe({
        next: (res) => {
          this.loading = false;
          this.showModalType = '';
          if (!res.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after deleting an assessment.';
            this.loadBuildingAssessments();
          }
        },
        error: (err) => {
          this.loading = false;
          this.showModalType = '';
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      })
    }
  } 
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }

}
